import styles from '@components/components/HeatInstallationForm/components/Result/Result.module.scss';
import ActionFooter from '@components/components/HeatInstallationForm/components/ActionFooter';
import InlineInfo from '@components/components/HeatInstallationForm/components/InlineInfo';
import React from 'react';
import classNames from 'classnames';
import {
  Accordion,
  Card,
  CardBody,
  Container,
  Content,
  Heading,
  IconCard,
  IconText,
  Steps,
} from '@wienenergiegithub/ui-next';

const Result = ({
  store,
  prev,
  step,
  handleGeneralNextStep,
  isGeneralRecommender,
  setNextValidStep,
}) => {
  const formatPrice = number =>
    new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'eur',
    })
      .format(number)
      .replace('€', '')
      .trim();

  const planningPrice = store.useValue('planningPrice');
  const basePrice = store.useValue('basePrice');
  const floorspace = store.useValue('size');
  const floorspacePrice = store.useValue('floorspacePrice');
  const radiatorCount = store.useValue('radiatorCount');
  const radiatorPrice = store.useValue('radiatorPrice');
  const totalPrice = store.useValue('totalPrice');
  const warmWaterDevice = store.useValue('warmWaterDevice');
  const waterDevicePrice = store.useValue('waterDevicePrice');

  // TODO refactor InlineInfo to new component variant IconText
  const infoTexts = (
    <div className={styles.infoTexts}>
      <InlineInfo
        content="Hierbei handelt es sich um eine unverbindliche Kostenschätzung auf Basis der von Ihnen getätigten Angaben. Gerne erstellen wir Ihnen ein verbindliches Angebot auf Basis einer persönlichen Beratung und Besichtigung vor Ort."
        icon="circle-info"
        size="md"
      />
      <InlineInfo
        content="Mögliche Demontage- und Entsorgungskosten für Ihre alte Heizung sind in dieser Berechnung noch nicht enthalten. Unser*e Mitarbeiter*innen beraten Sie gerne persönlich und informieren Sie über etwaige Zusatzkosten."
        icon="circle-info"
        size="md"
      />
    </div>
  );

  const accordionContent = (
    <>
      <div className={styles.tableRow}>
        <Content className={styles.tableLabel} size="lg">
          Planungskosten
        </Content>
        <Content
          className={classNames(styles.semi, styles.tablePrice)}
          size="lg"
        >
          {formatPrice(planningPrice)} EUR
        </Content>
      </div>
      <div className={styles.tableRow}>
        <Content className={styles.tableLabel} size="lg">
          Installationskosten
        </Content>
        <Content
          className={classNames(styles.semi, styles.tablePrice)}
          size="lg"
        >
          {formatPrice(basePrice + floorspace * floorspacePrice)} EUR
        </Content>
      </div>
      <div className={styles.tableRow}>
        <Content className={styles.tableLabel} size="lg">
          {radiatorCount || '0'} Heizkörper
        </Content>
        <Content
          className={classNames(styles.semi, styles.tablePrice)}
          size="lg"
        >
          {formatPrice(radiatorCount * radiatorPrice)} EUR
        </Content>
      </div>
      {warmWaterDevice !== 'Nein' && (
        <div className={styles.tableRow}>
          <Content className={styles.tableLabel} size="lg">
            Warmwassergerät
          </Content>
          <Content
            className={classNames(styles.semi, styles.tablePrice)}
            size="lg"
          >
            {formatPrice(waterDevicePrice)} EUR
          </Content>
        </div>
      )}

      <div className={styles.tableRow}>
        <Content
          className={classNames(styles.tableLabel, styles.semi)}
          size="lg"
        >
          Gesamtkosten
        </Content>
        <Content
          className={classNames(styles.semi, styles.tablePrice)}
          size="lg"
        >
          {formatPrice(totalPrice)} EUR
        </Content>
      </div>
    </>
  );

  return (
    <Container size="lg">
      <div
        className={classNames(styles.container, styles.row, {
          [styles.containerGeneral]: isGeneralRecommender,
          [styles.containerDefault]: !isGeneralRecommender,
        })}
      >
        {/* Grid Area Header */}
        <div className={styles.header}>
          <Heading className={styles.semi} size="h2">
            Ihre vorläufige Kostenkalkulation {isGeneralRecommender && '(3/4)'}
          </Heading>
          <Content className={styles.subHeader}>
            Basierend auf Ihren Angaben beträgt die vorläufige
            Kostenkalkulation:
          </Content>
        </div>
        {/* Grid Area Kosten */}
        <Card className={styles.kosten}>
          <CardBody>
            <Heading as="h3" className={styles.costsLabel} size="h5">
              Gesamtkosten
            </Heading>
            <Heading
              as="h4"
              className={classNames(styles.semi, styles.totalPrice)}
              noSpacing
              size="h1"
            >
              {formatPrice(totalPrice)} EUR
            </Heading>

            <Content className={styles.unit}>exkl. USt</Content>
            {isGeneralRecommender && (
              <>
                <div data-purpose="heat-installation-form-table-accordion">
                  <Accordion
                    className={styles.accordion}
                    disclosureSize="small"
                    title="Zusammensetzung der Kosten"
                  >
                    {accordionContent}
                  </Accordion>
                </div>
                {infoTexts}
              </>
            )}
          </CardBody>
        </Card>
        {/* Grid Area Info */}
        {!isGeneralRecommender && (
          <div className={styles.info}>{infoTexts}</div>
        )}
        {/* Grid Area Accordion */}
        {!isGeneralRecommender && (
          <div data-purpose="heat-installation-form-table-accordion">
            <Accordion
              className={styles.accordion}
              title="Zusammensetzung der Kosten"
            >
              {accordionContent}
            </Accordion>
          </div>
        )}

        {/* Grid Area Accordion */}
        <div className={styles.foerderInfo}>
          <Card>
            <CardBody>
              <Heading size="h4">
                Der Umstieg auf Fernwärme wird gefördert! Wir informieren Sie
                gerne im persönlichen Gespräch.
              </Heading>
              <Content noSpacing size="lg">
                Wir informieren Sie im Rahmen eines unverbindlichen
                Beratungsgesprächs über mögliche Förderungen.
              </Content>
            </CardBody>
          </Card>
        </div>
      </div>
      <div className={styles.nextStep}>
        <Heading size="h3">Ihre nächsten Schritte</Heading>
        <Content size="sm">
          Gerne erstellen wir Ihnen ein verbindliches Angebot auf Basis einer
          persönlichen Beratung und Besichtigung vor Ort.
        </Content>

        <Steps
          items={[
            {
              index: '1',
              title: 'Beratung anfragen',
              content: ' ',
            },
            {
              index: '2',
              title: 'Nachinstallation durchführen',
              content: ' ',
            },
            {
              index: '3',
              title: 'Fernwärme anmelden',
              content: ' ',
            },
          ]}
        />
      </div>
      <Heading size="h3">Ihre Vorteile</Heading>
      <Content>
        Sie müssen sich um nichts kümmern. Wir erledigen für Sie:
      </Content>
      {!isGeneralRecommender && (
        <div className={classNames(styles.gridCol3, styles.row)}>
          <IconCard
            glyph="edit"
            headingSize="h6"
            title="Genehmigungen der Haus-verwaltung"
            variant="iconTop"
          />
          <IconCard
            glyph="tools"
            headingSize="h6"
            title="Beauftragung/Kontrolle des Installateurs"
            variant="iconTop"
          />
          <IconCard
            glyph="euro-sign"
            headingSize="h6"
            title="Abrechnung mit dem Installateur"
            variant="iconTop"
          />
        </div>
      )}
      {isGeneralRecommender && (
        <div className={classNames(styles.gridCol4, styles.row)}>
          <IconText
            glyph="edit"
            heading="Genehmigungen der Haus&shy;verwaltung"
          />
          <IconText
            glyph="tools"
            heading="Beauftragung/&#8203;Kontrolle des Installateurs"
          />
          <IconText
            glyph="euro-sign"
            heading="Abrechnung mit dem Installateur"
          />
        </div>
      )}

      <ActionFooter
        handleSkipStep={handleGeneralNextStep}
        isGeneralRecommender={isGeneralRecommender}
        nextText="Unverbindliche Beratung Anfragen"
        prev={prev}
        setNextValidStep={setNextValidStep}
        step={step}
        stepName="ergebnis"
        store={store}
      />
    </Container>
  );
};

export default Result;
